import { useNavigationContainerRef } from 'expo-router';
import { useEffect } from 'react';

import {
  browserLedgerStorage,
  currentHistoryEntryId,
  recordHistoryEntry,
  resetHistoryLedger,
} from '@/util/historyLedger';

function loadedByFreshNavigation() {
  try {
    const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

    return !entry || entry.type === 'navigate' || entry.type === 'prerender';
  } catch {
    return true;
  }
}

/**
 * Keeps the tab's history ledger in step with the entry the app stands on, so `useGoBack` can tell
 * whether going back stays inside the app. Mounted once, in the root layout.
 *
 * A document loaded by a fresh navigation (a typed address, a link from another site) starts the
 * ledger over, since the entry before it may belong to another site. A reload or a return through
 * back and forward keeps it, which is what lets Back still go back after a reload.
 *
 * The router stamps ids from its own effects and state listener, which run after this hook's
 * effect, so every reading waits one task for the browser's history to catch up. The fresh start
 * applies to the first id seen, not to whatever the entry held when this mounted.
 */
export function useHistoryLedger() {
  const navigationRef = useNavigationContainerRef();

  useEffect(() => {
    const storage = browserLedgerStorage();

    let startsFresh = loadedByFreshNavigation();

    let pending: ReturnType<typeof setTimeout> | undefined;

    let arrivedByTraversal = false;

    const record = () => {
      clearTimeout(pending);

      pending = setTimeout(() => {
        const id = currentHistoryEntryId();

        if (!id) {
          return;
        }

        if (startsFresh) {
          startsFresh = false;

          resetHistoryLedger(storage, id);

          return;
        }

        recordHistoryEntry(storage, id, { traversal: arrivedByTraversal });

        arrivedByTraversal = false;
      }, 0);
    };

    const recordTraversal = () => {
      arrivedByTraversal = true;

      record();
    };

    record();

    const unsubscribe = navigationRef.addListener('state', record);

    window.addEventListener('popstate', recordTraversal);

    return () => {
      clearTimeout(pending);

      unsubscribe();

      window.removeEventListener('popstate', recordTraversal);
    };
  }, [navigationRef]);
}
